/**
 * Account control and persistent escape hatch, including shell-less screens.
 * In the account panel and the phone drawer the control is the panel's own
 * rows: the two sample personas (the one in force washed cobalt with a check,
 * as FocusPicker marks its choice) and Change cohort, which names the cohort
 * those rows open.
 *
 * Inside a sample, the persistent bar wears the amber wash every borrowed
 * view wears (DESIGN.md §4) and holds one line: a chip naming the sample,
 * which opens the chooser as FocusPicker's chip opens its picker, and Back
 * to super admin. The chip is the one thing on the line that shrinks.
 */
import { useLayoutEffect, useRef, useState } from 'react';
import type { SvgIconComponent } from '@mui/icons-material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { alpha, Box, Button, ButtonBase } from '@mui/material';

import {
  DEFAULT_SANDBOX_COHORT,
  type SandboxCohort,
  sandboxCohortKey,
  sandboxCohortLabel,
  type SandboxCommand,
  type SandboxSelection,
  type SandboxView,
  SandboxViewSchema,
  type SandboxWorkspaceSummary,
} from '@shared/sandbox';
import {
  clearSandboxSelection,
  documentSandboxInvalid,
  documentSandboxSelection,
  lastSandboxForCohort,
  selectSandbox,
} from '@/auth/sandboxSelection';
import {
  BOTTOM_NAV_HEIGHT_VAR,
  SANDBOX_BAR_HEIGHT_VAR,
} from '@/config/navigation';
import { useAuth } from '@/hooks/useAuth';
import { fetchWithConfig } from '@/services/apiUtils';

import { accountRowSx } from './accountPanelStyles';
import SandboxViewDialog from './SandboxViewDialog';

const PERSONAS: Record<SandboxView, { label: string; Icon: SvgIconComponent }> =
  {
    teacher: { label: 'Sample teacher', Icon: SchoolOutlinedIcon },
    student: { label: 'Sample student', Icon: PersonOutlineIcon },
  };

const currentWash = 'color-mix(in srgb, var(--cobalt) 10%, transparent)';

/** Whether the account control offers sample views; hosts draw their rule from it. */
export const useSandboxEntry = () => Boolean(useAuth().sandboxView?.available);

export default function SandboxViewSwitch({
  persistent = false,
}: {
  persistent?: boolean;
}) {
  const { sandboxView, getAccessToken } = useAuth();
  const active = sandboxView?.selection ?? documentSandboxSelection;
  const [open, setOpen] = useState(false);
  const [cohort, setCohort] = useState<SandboxCohort>(
    sandboxView?.workspace?.cohort ?? DEFAULT_SANDBOX_COHORT
  );
  const [view, setView] = useState<SandboxView>(active?.view ?? 'teacher');
  // The view being opened; the page reloads once it lands.
  const [opening, setOpening] = useState<SandboxView | null>(null);
  const busy = opening !== null;
  const [error, setError] = useState<string | null>(null);
  const banner = useRef<HTMLDivElement>(null);
  const visible = persistent && Boolean(active || documentSandboxInvalid);
  useLayoutEffect(() => {
    if (!visible || !banner.current) return;
    const element = banner.current;
    const update = () =>
      document.documentElement.style.setProperty(
        SANDBOX_BAR_HEIGHT_VAR,
        `${element.getBoundingClientRect().height}px`
      );
    update();
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(element);
    return () => {
      observer?.disconnect();
      document.documentElement.style.removeProperty(SANDBOX_BAR_HEIGHT_VAR);
    };
  }, [visible]);
  if (persistent ? !active && !documentSandboxInvalid : !sandboxView?.available)
    return null;

  const choose = async (
    chosenView: SandboxView,
    fresh = false,
    sameWorkspace = false
  ) => {
    if (busy) return;
    setOpening(chosenView);
    setError(null);
    try {
      const currentCohort = sandboxView?.workspace?.cohort;
      const keepWorkspace =
        sameWorkspace ||
        (!fresh &&
          active &&
          currentCohort &&
          sandboxCohortKey(currentCohort) === sandboxCohortKey(cohort));
      const workspaceId = keepWorkspace
        ? active?.workspaceId
        : fresh
          ? undefined
          : lastSandboxForCohort(cohort);
      const input = {
        view: chosenView,
        ...(workspaceId ? { workspaceId } : { cohort, fresh }),
      };
      // Retained across network errors AND reloads; retrying Start fresh is safe.
      const keyName = `deepthink.sandbox.request:${JSON.stringify(input)}`;
      const requestKey = sessionStorage.getItem(keyName) ?? crypto.randomUUID();
      sessionStorage.setItem(keyName, requestKey);
      const result = await fetchWithConfig<{
        selection: SandboxSelection;
        workspace: SandboxWorkspaceSummary;
      }>('/users/me/sandbox-view', {
        method: 'POST',
        authToken: await getAccessToken(),
        body: JSON.stringify({ ...input, requestKey } satisfies SandboxCommand),
      });
      selectSandbox(result.selection, result.workspace.cohort);
      sessionStorage.removeItem(keyName);
      window.location.assign(
        chosenView === 'teacher' ? '/teacher/homework' : '/tasks'
      );
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Could not open sample view. Try again.'
      );
      setOpening(null);
    }
  };
  const exit = async () => {
    // Selection is local, not an authenticated server session. Clearing it is
    // safe even when the token expired or the owner lost their capability.
    let timeout: ReturnType<typeof setTimeout> | undefined;
    try {
      await Promise.race([
        (async () => {
          await fetchWithConfig('/users/me/sandbox-view', {
            method: 'DELETE',
            authToken: await getAccessToken(),
            retry: false,
          });
        })(),
        // An unavailable authentication provider must not trap this tab.
        new Promise<void>(resolve => {
          timeout = setTimeout(resolve, 1500);
        }),
      ]);
    } catch {
      /* a revoked or expired session must still have an exit */
    } finally {
      clearTimeout(timeout);
      clearSandboxSelection();
      window.location.assign('/');
    }
  };
  const openChooser = () => {
    setCohort(sandboxView?.workspace?.cohort ?? cohort);
    setError(null);
    setOpen(true);
  };
  const barLabel = documentSandboxInvalid
    ? 'Sample view unavailable'
    : PERSONAS[active?.view ?? 'student'].label;
  const barCohort = sandboxView?.workspace
    ? sandboxCohortLabel(sandboxView.workspace.cohort)
    : null;
  const controls = (
    <>
      {persistent ? (
        <>
          <ButtonBase
            aria-haspopup="dialog"
            aria-label={`Change view: ${[barLabel, barCohort].filter(Boolean).join(', ')}`}
            onClick={openChooser}
            sx={{
              position: 'relative',
              display: 'inline-flex',
              gap: '6px',
              flex: '0 1 auto',
              minWidth: 0,
              height: 32,
              pl: '10px',
              pr: '6px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-pill)',
              backgroundColor: 'var(--paper)',
              boxShadow: 'var(--shadow-soft)',
              color: 'var(--ink)',
              font: 'inherit',
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: '-0.01em',
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              transition:
                'background-color 200ms var(--ease-default, ease-out)',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              '&:hover': { backgroundColor: 'var(--bg-alt)' },
              '&:focus-visible': {
                outline: '2px solid var(--cobalt)',
                outlineOffset: 2,
              },
              // A compact chip with the bar's 48px touch target.
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: '-8px 0',
              },
              '& .MuiSvgIcon-root': {
                flexShrink: 0,
                fontSize: 18,
                color: 'var(--ink-muted)',
              },
            }}
          >
            <VisibilityIcon aria-hidden />
            <Box
              component="span"
              sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              <span>{barLabel}</span>
              {barCohort ? (
                // A phone keeps the line for the view and the way back.
                <Box
                  component="span"
                  sx={{
                    display: { xs: 'none', sm: 'inline' },
                    color: 'var(--ink-muted)',
                    fontWeight: 600,
                  }}
                >
                  {` · ${barCohort}`}
                </Box>
              ) : null}
            </Box>
            <ExpandMoreIcon aria-hidden />
          </ButtonBase>
          <Button
            size="small"
            onClick={() => void exit()}
            sx={{
              flexShrink: 0,
              minHeight: 48,
              px: '10px',
              fontSize: 13,
              fontWeight: 800,
              whiteSpace: 'nowrap',
            }}
          >
            Back to super admin
          </Button>
        </>
      ) : (
        <Box role="group" aria-label="Sample views">
          {SandboxViewSchema.options.map(persona => {
            const { label, Icon } = PERSONAS[persona];
            const current = active?.view === persona;
            return (
              <ButtonBase
                key={persona}
                aria-current={current || undefined}
                aria-disabled={busy || undefined}
                onClick={() => void choose(persona, false, Boolean(active))}
                sx={[
                  accountRowSx,
                  current && {
                    backgroundColor: currentWash,
                    '&:hover': { backgroundColor: currentWash },
                    '& .MuiSvgIcon-root.sandbox-current': {
                      color: 'var(--cobalt)',
                    },
                  },
                ]}
              >
                <Icon aria-hidden />
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                  {opening === persona ? 'Opening…' : label}
                </Box>
                {current ? (
                  <CheckRoundedIcon aria-hidden className="sandbox-current" />
                ) : null}
              </ButtonBase>
            );
          })}
          <ButtonBase
            aria-haspopup="dialog"
            onClick={openChooser}
            sx={[accountRowSx, { py: '6px' }]}
          >
            <TuneRoundedIcon aria-hidden />
            <Box component="span" sx={{ minWidth: 0 }}>
              Change cohort
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: 'var(--ink-muted)',
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: 0,
                }}
              >
                {sandboxCohortLabel(sandboxView?.workspace?.cohort ?? cohort)}
              </Box>
            </Box>
          </ButtonBase>
          {error ? (
            <Box
              role="alert"
              sx={{
                px: '12px',
                pt: '4px',
                pb: '8px',
                color: 'error.main',
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1.45,
              }}
            >
              {error}
            </Box>
          ) : null}
        </Box>
      )}
      <SandboxViewDialog
        open={open}
        busy={busy}
        error={error}
        view={view}
        cohort={cohort}
        onViewChange={setView}
        onCohortChange={setCohort}
        onOpen={() => void choose(view)}
        onStartFresh={() => void choose(view, true)}
        onClose={() => setOpen(false)}
      />
    </>
  );
  return persistent ? (
    <Box
      ref={banner}
      role="region"
      aria-label="Sample view"
      sx={theme => {
        const wash = alpha(theme.palette.warning.dark, 0.16);
        return {
          position: 'fixed',
          bottom: `var(${BOTTOM_NAV_HEIGHT_VAR}, 0px)`,
          left: 0,
          right: 0,
          zIndex: theme.zIndex.drawer + 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          minHeight: 48,
          px: '12px',
          // Above the phone bottom bar, which carries the safe area itself;
          // pinned to the viewport foot, the strip clears it on its own.
          pb: `max(0px, calc(var(--safe-area-inset-bottom, 0px) - var(${BOTTOM_NAV_HEIGHT_VAR}, 0px)))`,
          // Laid over paper: the strip floats above the page.
          background: `linear-gradient(${wash}, ${wash}), var(--paper)`,
          borderTop: `1px solid ${alpha(theme.palette.warning.dark, 0.55)}`,
        };
      }}
    >
      {controls}
    </Box>
  ) : (
    controls
  );
}
